import './index.css';
import Navbar from '../Navbar2';
import { useState, useEffect } from 'react';
import { db, auth } from '../../firebaseConfig';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';

const UserBookings = () => {
  const [activeTab, setActiveTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [pastBookings, setPastBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = auth.currentUser;
        if (!user) {
          setError('User not authenticated. Please log in.');
          setLoading(false);
          return;
        }

        const userId = user.uid;
        const bookingsQuery = query(
          collection(db, 'bookings'),
          where('userId', '==', userId),
        );
        const querySnapshot = await getDocs(bookingsQuery);
        const fetchedBookings = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          checkIn: doc.data().checkIn.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
          checkOut: doc.data().checkOut.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
          createdAt: doc.data().createdAt.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
          }),
        }));

        const currentDate = new Date();
        const active = fetchedBookings.filter(
          (booking) =>
            booking.status.toLowerCase() === 'confirmed' &&
            new Date(booking.checkOut) >= currentDate
        );
        const cancelled = fetchedBookings.filter(
          (booking) => booking.status.toLowerCase() === 'cancelled'
        );
        const past = fetchedBookings.filter(
          (booking) =>
            (booking.status.toLowerCase() === 'confirmed' ||
             booking.status.toLowerCase() === 'completed') &&
            new Date(booking.checkOut) < currentDate
        );

        setBookings(active);
        setCancellations(cancelled);
        setPastBookings(past);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Failed to load bookings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, []);

  const handleCancelClick = (bookingId) => {
    setSelectedBookingId(bookingId);
    setShowModal(true);
  };

  const handleCancelConfirm = async () => {
    try {
      const bookingRef = doc(db, 'bookings', selectedBookingId);
      await updateDoc(bookingRef, { status: 'cancelled' });
      const cancelledBooking = bookings.find((booking) => booking.id === selectedBookingId);
      setBookings(bookings.filter((booking) => booking.id !== selectedBookingId));
      if (cancelledBooking) {
        setCancellations([...cancellations, { ...cancelledBooking, status: 'cancelled' }]);
      }
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      console.error('Error cancelling booking:', err);
      setError('Failed to cancel booking. Please try again.');
    } finally {
      setShowModal(false);
      setSelectedBookingId(null);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedBookingId(null);
  };

  const toggleContactDetails = (bookingId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [bookingId]: !prev[bookingId],
    }));
  };

  const renderBookingList = (bookingsList, showCancelButton = false) => {
    if (bookingsList.length === 0) {
      return <p className="no-bookings">No bookings found.</p>;
    }

    return bookingsList.map((booking) => (
      <div key={booking.id} className="booking-item" onClick={() => toggleContactDetails(booking.id)}>
        <div className="booking-details-grid">
          <div className="booking-field">
            <strong>Villa Title:</strong> {booking.villaTitle}
          </div>
          <div className="booking-field">
            <strong>Check-In:</strong> {booking.checkIn}
          </div>
          <div className="booking-field">
            <strong>Check-Out:</strong> {booking.checkOut}
          </div>
          <div className="booking-field">
            <strong>Guests:</strong> {booking.totalGuests}
          </div>
          <div className="booking-field">
            <strong>Nights:</strong> {booking.nights}
          </div>
          <div className="booking-field">
            <strong>Total Price:</strong> ₹{booking.totalPrice.toLocaleString()}
          </div>
          <div className="booking-field">
            <strong>Booked On:</strong> {booking.createdAt}
          </div>
          <div className="booking-field">
            <strong>Status:</strong>
            <span className={`status-badge status-${booking.status.toLowerCase()}`}>
              {booking.status}
            </span>
          </div>
        </div>
        {expandedRows[booking.id] && (
          <div className="contact-details">
            <p><strong>Contact:</strong> {booking.contactDetails.firstName} {booking.contactDetails.lastName}</p>
            <p><strong>Email:</strong> {booking.contactDetails.email}</p>
            <p><strong>Phone:</strong> {booking.contactDetails.phoneNumber}</p>
          </div>
        )}
        {showCancelButton && (
          <div className="cancel-button-container">
            <button
              className="cancel-button"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelClick(booking.id);
              }}
            >
              Cancel Booking
            </button>
          </div>
        )}
      </div>
    ));
  };

  return (
    <>
      <Navbar />
      <div className="my-bookings">
        <h1>My Bookings</h1>
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            Active Bookings
          </button>
          <button
            className={`tab ${activeTab === 'cancellations' ? 'active' : ''}`}
            onClick={() => setActiveTab('cancellations')}
          >
            Cancellations
          </button>
          <button
            className={`tab ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
          >
            Past Bookings
          </button>
        </div>
        <div className="booking-content">
          {loading && <p className="loading">Loading bookings...</p>}
          {error && <p className="error">{error}</p>}
          {!loading && !error && (
            <>
              {activeTab === 'bookings' && (
                <div>
                  <h2>Active Bookings</h2>
                  {renderBookingList(bookings, true)}
                </div>
              )}
              {activeTab === 'cancellations' && (
                <div>
                  <h2>Cancellations</h2>
                  {renderBookingList(cancellations)}
                </div>
              )}
              {activeTab === 'past' && (
                <div>
                  <h2>Past Bookings</h2>
                  {renderBookingList(pastBookings)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {showModal && (
        <div className="modal-overlayb">
          <div className="modalb">
            <h3>Confirm Cancellation</h3>
            <p>Are you sure you want to cancel this booking?</p>
            <div className="modal-buttonsb">
              <button className="modal-cancelb" onClick={handleModalClose}>
                Cancel
              </button>
              <button className="modal-confirmb" onClick={handleCancelConfirm}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {showToast && (
        <div className="toast">
          Booking cancelled successfully!
        </div>
      )}
    </>
  );
};

export default UserBookings;